import { Switch as BaseSwitch } from '@base-ui/react/switch';
import { useId } from 'react';

/**
 * Interruptor liga/desliga do DS, sobre `@base-ui/react/switch` (role="switch",
 * teclado e aria prontos). Visual herdado do antigo `AttributeToggle` da tela
 * de Produtos: trilho preto/branco conforme o tema quando ligado.
 *
 * `busy` desabilita e troca o cursor para "aguarde" (gravação em voo);
 * `description` vira texto secundário abaixo do rótulo; `title` é o tooltip
 * (ex.: por que está desabilitado).
 */
const Switch = ({
    id,
    checked = false,
    onCheckedChange,
    disabled = false,
    busy = false,
    label = null,
    description = null,
    title,
    className = '',
}) => {
    const generatedId = useId();
    const switchId = id || generatedId;
    const descriptionId = description ? `${switchId}-description` : undefined;
    const isDisabled = disabled || busy;

    const control = (
        <BaseSwitch.Root
            id={switchId}
            checked={Boolean(checked)}
            onCheckedChange={(next) => onCheckedChange?.(Boolean(next))}
            disabled={isDisabled}
            aria-describedby={descriptionId}
            aria-label={typeof label === 'string' ? label : undefined}
            title={title}
            className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-mono-500 focus-visible:ring-offset-2 ${
                checked
                    ? 'border-black bg-black dark:border-white dark:bg-white'
                    : 'border-mono-300 bg-mono-100 dark:border-mono-700 dark:bg-mono-900'
            } ${isDisabled ? (busy ? 'cursor-wait opacity-60' : 'cursor-not-allowed opacity-60') : 'cursor-pointer'}`}
        >
            <BaseSwitch.Thumb
                className={`absolute top-0.5 h-3.5 w-3.5 rounded-full transition-transform ${
                    checked ? 'translate-x-[18px] bg-white dark:bg-black' : 'translate-x-0.5 bg-mono-400'
                }`}
            />
        </BaseSwitch.Root>
    );

    if (!label && !description) {
        return control;
    }

    return (
        <div className={`flex items-start gap-3 ${className}`} title={title}>
            <div className="pt-0.5">{control}</div>
            <div className="min-w-0">
                {label ? (
                    <label
                        htmlFor={switchId}
                        className={`block text-sm font-medium text-mono-700 dark:text-mono-300 ${
                            isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
                        }`}
                    >
                        {label}
                    </label>
                ) : null}
                {description ? (
                    <p id={descriptionId} className="mt-0.5 text-xs text-mono-500 dark:text-mono-400">
                        {description}
                    </p>
                ) : null}
            </div>
        </div>
    );
};

export default Switch;
